"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = LeadGenForm;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _index = _interopRequireDefault(require("../form-group/index"));
var _core = require("@linaria/core");
var _image = require("@site/pattern-library-core/image");
var _react2 = require("@linaria/react");
var _utils = require("./utils");
var _patternLibraryUtils = require("@site/pattern-library-utils");
/* eslint-disable complexity */

const styledDropDown = "pl-si1xt1e";
const FormWrapper = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "FormWrapper",
  class: "pl-fvzrzic"
});

/**
 * LeadGenForm Component
 *
 * @param {String} props.text Text rendered on the button
 * @param {String} props._name Utilized for click-through tracking; only accepts alphanumeric string and underscores
 * @param {String} props.width CTA width
 * @param {Array} props.fields The form fields to collect from the user, links to a lead gen form field
 * @param {String} props.ajaxUrl The API endpoint to post the form payload, should be LMAPI to post to Salesforce
 * @param {String} props.heading The title text rendered on the form modal
 * @param {Function} props.onError Function ran on error
 * @param {String} props.leadType Deprecated with pipedrive, do not use
 * @param {Function} props.onSubmit Function run on submit
 * @param {String} props.errorText The descriptive error text rendered on the modal
 * @param {Function} props.onSuccess Function run on success
 * @param {Array} props.questions Today we only use this selector field to add the annual sales dropdown field to the form
 * @param {String} props.subheading The descriptive text rendered below the title text on the form modal
 * @param {Object} props.modalHeader The asset rendered above the title text on the form modal
 * @param {String} props.payMeNumber The contact number rendered immediately after the subheading
 * @param {String} props.productIntent Routes the lead to the correct Salesforce instance
 * @param {String} props.leadSource A descriptive label used for first-level sorting/catgeorization in Salesforce
 * @param {String} props.leadName A descriptive label for the lead to assist with sorting in Salesforce
 * @param {String} props.campaignName  A descriptive label for a campaign, primarily useful for campaign management
 * @param {String} props.campaignId The unique numeric ID in ad platforms (e.g. Meta, Google, etc) associated with a campaign
 * @param {Object} props.dealPipedriveIds Deprecated with pipedrive, do not use
 * @param {Object} props.personPipedriveIds Deprecated with pipedrive, do not use
 * @param {Object} props.pipedriveDataFields Deprecated with pipedrive, do not use
 * @returns {React.ReactElement} LeadGenForm Component
 * @public
 */
function LeadGenForm({
  text,
  _name,
  width = 'auto',
  fields = [],
  ajaxUrl,
  heading,
  onError,
  leadType = '',
  onSubmit,
  errorText,
  onSuccess,
  questions = [],
  subheading,
  modalHeader,
  payMeNumber,
  productIntent = '',
  leadSource = '',
  leadName = '',
  campaignName = '',
  campaignId = '',
  dealPipedriveIds = '',
  personPipedriveIds = '',
  pipedriveDataFields = ''
}) {
  questions.forEach((q, i) => {
    const key = `question-${i}`;
    if (!fields.some(field => field.key === key)) {
      fields.push({
        key,
        type: 'dropdown',
        ...q,
        className: styledDropDown,
        name: q.name || q._name,
        validate: v => q.items.some(item => item.value === v)
      });
    }
  });
  const getAjaxUrlType = url => {
    const {
      Value: urlValue
    } = url || {};
    if (urlValue?.toLowerCase().includes('leadcapture')) {
      return _utils.AjaxUrlType.LEAD_CAPTURE;
    }
    if (urlValue?.toLowerCase().includes('mmc_request_call_modal_payments')) {
      return _utils.AjaxUrlType.MMC_REQUEST_CALL_MODAL_PAYMENTS;
    }
    return null;
  };
  const getLeadCaptureDataFields = entityType => {
    if (!pipedriveDataFields) {
      return {};
    }
    const pipedriveIds = entityType === _utils.PipedriveEntityType.DEAL ? dealPipedriveIds : personPipedriveIds;
    return Object.keys(pipedriveDataFields)?.reduce((acc, field) => {
      const currentPipedriveId = pipedriveIds[field];
      if (currentPipedriveId) {
        delete pipedriveIds[field];
        return {
          ...acc,
          [currentPipedriveId]: pipedriveDataFields[field]
        };
      }
    }, {});
  };
  const getMmcRequestCallDataFields = (entityType, formData) => {
    const questionsForPipedriveEntity = questions?.filter(question => entityType === _utils.PipedriveEntityType.DEAL ? question.pipedriveDealField : question.pipedrivePersonField);
    return questionsForPipedriveEntity.reduce((question, result) => ({
      ...result,
      [question._name]: formData[questions._name]
    }), {});
  };
  const getDataFields = (entityType, formData) => {
    switch (getAjaxUrlType(ajaxUrl)) {
      case _utils.AjaxUrlType.LEAD_CAPTURE:
        return getLeadCaptureDataFields(entityType);
      case _utils.AjaxUrlType.MMC_REQUEST_CALL_MODAL_PAYMENTS:
        return getMmcRequestCallDataFields(entityType, formData);
      default:
        return null;
    }
  };
  const getParams = () => new Proxy(new URLSearchParams(global?.location?.search), {
    get: (searchParams, prop) => searchParams.get(prop)
  });
  const handleSubmit = data => {
    const {
      campaignName: legacyCampaignName = '',
      createOrg = false,
      defaultTitle = '',
      leadSource: legacyLeadSource = '',
      pipelineId,
      pipedriveInstance,
      pipedriveUserId
    } = leadType;
    const isPipedrive = productIntent === '';
    if (isPipedrive) {
      data.traceId = document?.getElementsByName('gd:traceId')?.[0]?.content || '';
      data.leadSource = legacyLeadSource?.Value ? legacyLeadSource?.Value : legacyLeadSource;
      data.utmCampaign = legacyCampaignName || getParams()?.utmCampaign || getParams()?.utm_campaign || '';
      data.title = defaultTitle;
      data.userId = pipedriveUserId;
      data.pipedrive = {
        pipelineId,
        pipedriveInstance,
        createOrg,
        dealFields: getDataFields(_utils.PipedriveEntityType.DEAL, data),
        personFields: getDataFields(_utils.PipedriveEntityType.PERSON, data),
        dealPipedriveIds: (0, _utils.filterObjectKeys)(dealPipedriveIds, ['id', '_name', 'templateName']),
        personPipedriveIds: (0, _utils.filterObjectKeys)(personPipedriveIds, ['id', '_name', 'templateName'])
      };
    } else {
      data.leadSource = leadSource?.Value;
      data.productIntent = productIntent?.Value;
      data.leadName = leadName;
      data.campaignId = campaignId;
      data.campaignName = campaignName || getParams()?.utmCampaign || getParams()?.utm_campaign || '';
      data.utmMedium = getParams()?.utmMedium || getParams()?.utm_medium || '';
      data.utmTerm = getParams()?.utmTerm || getParams()?.utm_term || '';
      data.utmSource = getParams()?.utmSource || getParams()?.utm_source || '';
      data.utmContent = getParams()?.utmContent || getParams()?.utm_content || '';
      data.adClickId = getParams()?.gclid || getParams()?.fbclid || '';
    }
    data.phoneNumber = data.phone;
    return onSubmit(data);
  };
  return /*#__PURE__*/_react.default.createElement(FormWrapper, null, /*#__PURE__*/_react.default.createElement(_index.default, {
    id: _name,
    _name: _name,
    width: width,
    fields: fields,
    ajaxUrl: ajaxUrl,
    heading: heading,
    onError: onError,
    onSubmit: handleSubmit,
    onSuccess: onSuccess,
    recaptcha: 'off',
    submitText: text,
    description: subheading,
    eyebrowLogo: {
      url: modalHeader?.url,
      altText: modalHeader?.altText
    },
    formErrorText: errorText,
    descriptionAfterContent: payMeNumber
  }));
}
LeadGenForm.propTypes = {
  text: _propTypes.default.string,
  _name: _propTypes.default.string,
  userId: _propTypes.default.string,
  width: _propTypes.default.oneOf(['auto', 'full']),
  fields: _propTypes.default.arrayOf(_propTypes.default.shape({
    id: _propTypes.default.string,
    type: _propTypes.default.string,
    name: _propTypes.default.string,
    _name: _propTypes.default.string,
    label: _propTypes.default.string,
    dataCy: _propTypes.default.string,
    required: _propTypes.default.bool,
    placeholder: _propTypes.default.string,
    requiredText: _propTypes.default.string,
    showCountryCode: _propTypes.default.bool
  })),
  ajaxUrl: _propTypes.default.shape({
    Value: _propTypes.default.string
  }),
  heading: _propTypes.default.string,
  onError: _propTypes.default.func,
  leadType: _propTypes.default.object,
  onSubmit: _propTypes.default.func,
  errorText: _propTypes.default.string,
  onSuccess: _propTypes.default.func,
  questions: _propTypes.default.arrayOf(_propTypes.default.shape({
    label: _propTypes.default.string,
    required: _propTypes.default.bool,
    items: _propTypes.default.arrayOf(_propTypes.default.shape({
      text: _propTypes.default.string,
      value: _propTypes.default.string,
      id: _propTypes.default.string,
      _name: _propTypes.default.string,
      expectedSignedaGPV: _propTypes.default.string,
      templateName: _propTypes.default.string
    })),
    pipedriveDealField: _propTypes.default.bool,
    pipedrivePersonField: _propTypes.default.bool,
    pipedriveFieldId: _propTypes.default.string,
    placeholder: _propTypes.default.string,
    id: _propTypes.default.string,
    _name: _propTypes.default.string,
    name: _propTypes.default.string,
    templateName: _propTypes.default.string
  })),
  subheading: _propTypes.default.string,
  modalHeader: _propTypes.default.shape(_image.imgPropShape),
  payMeNumber: _propTypes.default.string,
  updateAnswer: _propTypes.default.func,
  onCustomValidation: _propTypes.default.func,
  productIntent: _propTypes.default.object,
  leadSource: _propTypes.default.object,
  leadName: _propTypes.default.string,
  campaignName: _propTypes.default.string,
  campaignId: _propTypes.default.string,
  dealPipedriveIds: _propTypes.default.shape({
    MarketId: _propTypes.default.string,
    PrivateLabelId: _propTypes.default.string,
    VisitorId: _propTypes.default.string,
    CustomerId: _propTypes.default.string,
    id: _propTypes.default.string,
    _name: _propTypes.default.string,
    templateName: _propTypes.default.string
  }),
  personPipedriveIds: _propTypes.default.shape({
    CustomerId: _propTypes.default.string,
    id: _propTypes.default.string,
    _name: _propTypes.default.string,
    templateName: _propTypes.default.string
  }),
  pipedriveDataFields: _propTypes.default.object
};